import { useEffect, useMemo, useState } from "react";
import { Loader2, Layers, ChevronRight, ExternalLink, RefreshCw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import DetailDrawer from "@/components/DetailDrawer";

/**
 * CustomJobsCard
 *  - Lists recent /custom-submissions from the production backend.
 *  - Row-tap opens DetailDrawer (readonly + PDF/Email actions).
 *  - The mobile app calls these "custom jobs" (bucket sheets, GET wear, etc).
 */
export default function CustomJobsCard() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);

  const reload = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/custom-submissions");
      const arr = Array.isArray(data) ? data : data?.items || data?.results || data?.data || [];
      setItems(arr);
    } catch (ex) {
      setErr(apiError(ex));
      setItems([]);
    } finally { setBusy(false); }
  };

  useEffect(() => { reload().catch(() => {}); }, []);

  const recent = useMemo(() => (items || []).slice(0, 8), [items]);

  return (
    <section className="panel-heavy p-5 md:p-6" data-testid="custom-jobs-card">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center"
             style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
          <Layers className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted">Custom jobs</div>
          <div className="font-display font-black text-xl tracking-tight">
            Bucket sheets, GET wear &amp; site forms
            {items && <span className="ml-2 text-sm text-muted font-mono-tight">· {items.length}</span>}
          </div>
        </div>
        <button className="btn btn-sm btn-ghost" onClick={reload} disabled={busy} data-testid="custom-jobs-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      {err && (
        <div className="pill pill-red !text-sm !py-2 !px-3 justify-start mb-3">{err}</div>
      )}

      {items === null ? (
        <div className="panel-flat p-8 flex items-center justify-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      ) : recent.length === 0 ? (
        <div className="panel-flat p-8 text-center" data-testid="custom-jobs-empty">
          <ExternalLink className="w-8 h-8 mx-auto mb-2 text-muted" />
          <div className="font-display font-black text-lg">No custom jobs yet</div>
          <div className="text-muted text-sm mt-1">
            Bucket sheets, GET wear entries, or any site-specific form the crew
            submits will appear here.
          </div>
        </div>
      ) : (
        <div className="grid gap-2" data-testid="custom-jobs-list">
          {recent.map((it, i) => (
            <button
              key={it.id || it._id || i}
              type="button"
              onClick={() => setSelected(it)}
              className="panel p-3.5 text-left flex items-center gap-3 group"
              data-testid={`custom-job-${it.id || i}`}
            >
              <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center"
                   style={{ background: "rgba(245,130,32,0.12)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.25)" }}>
                <Layers className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-black text-sm truncate">
                  {it.module_name || it.title || it.name || `Module ${String(it.module_id || "").slice(0, 8)}`}
                </div>
                <div className="text-[11px] font-mono-tight text-muted truncate">
                  {(it.asset_id || it.asset_name) && <>{it.asset_id || it.asset_name} · </>}
                  {it.created_by_name || it.submitted_by || ""}
                  {it.created_at && <> · {fmtDate(it.created_at)}</>}
                </div>
              </div>
              {it.status && <span className="pill !text-[10px] !py-0.5 !px-2">{it.status}</span>}
              <ChevronRight className="w-4 h-4 text-muted group-hover:text-white transition-colors" />
            </button>
          ))}
        </div>
      )}

      {selected && (
        <DetailDrawer
          item={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => { setSelected(null); reload(); }}
          itemLabel={(it) => it.module_name || it.title || "Custom Job"}
          resourceName="Custom Job"
        />
      )}
    </section>
  );
}

function fmtDate(v) { if (!v) return null; try { return new Date(v).toLocaleString(); } catch { return String(v); } }
